import React from "react";
import { setOrdersParams } from "../../entities/actions";
import { OrderWithSelector } from "../common/Order/ProgressOrder/orderWithSelector";
import { ProgressBar } from "../common/ProgressBar";
import cloneDeep from "lodash/cloneDeep";
import { set } from "lodash";
import { orderTypeEnum } from "../consts";
import { rnd } from "../MainPage";
import { config } from "../../config";
import { generateSteppedYArray } from "../../utils/getRandomTypeAndRate";

export const OrderRow = ({
  order,
  i,
  streamOrders,
  orderQty,
  simulation,
  resetValue,
  setResetValue,
  setValue,
  dispatch,
  floorQuantity,
  currentTick,
  quantityRef,
  steps,
  updateOrder,
  showLSHOption
}) => {
  const color = { SELL: "#7e7d7d", BUY: "#FFFFFF" };

  const changeOrderCap = (e, prop) => {
    const currentOrder = cloneDeep(orderQty[e.target.id]);
    set(currentOrder, `streamRate.${prop}`, +e.target.value);
    const newState = {
      ...orderQty,
      [e.target.id]: currentOrder
    };
    setValue(newState);
  };

  const setOrderCap = (e, prop) => {
    const currentOrder = cloneDeep(orderQty[e.target.id]);

    const value = e.target.value;

    if (currentOrder.type === orderTypeEnum.xy) {
      const streamRateKey = `streamRate.${prop}`;
      const maxLimit = 500;
      const minLimit = 1;

      const oppositeProp = prop === "x" ? "y" : "x";

      if (value > maxLimit) {
        // user can not set a value more than limit
        set(currentOrder, streamRateKey, maxLimit);
      } else if (value < minLimit) {
        // user can not set a value less than limit
        set(currentOrder, streamRateKey, minLimit);
      } else if (
        currentOrder.streamRate?.[oppositeProp] &&
        ((prop === "x" && value > currentOrder.streamRate[oppositeProp]) ||
          (prop === "y" && value < currentOrder.streamRate[oppositeProp]))
      ) {
        // X can not be more than Y and Y can not be less than X
        set(currentOrder, streamRateKey, currentOrder.streamRate[oppositeProp]);
      }
    } else if (currentOrder.type === orderTypeEnum.lsh) {
      const streamRateKey = `streamRate.${prop}`;
      const maxLimit = 100;
      const minLimit = 1;

      if (value > maxLimit) {
        // user can not set a value more than limit
        set(currentOrder, streamRateKey, maxLimit);
      } else if (value < minLimit) {
        // user can not set a value less than limit
        set(currentOrder, streamRateKey, minLimit);
      }
    }
    const newState = {
      ...orderQty,
      [e.target.id]: currentOrder
    };

    setValue(newState);

    dispatch(setOrdersParams(newState));
  };

  const changeOrderType = e => {
    const currentOrder = cloneDeep(orderQty[e.target.id]);
    set(currentOrder, "type", e.target.value);
    switch (e.target.value) {
      case orderTypeEnum.lsh:
        set(currentOrder, "streamRate.x", 5);
        set(currentOrder, "streamRate.y", undefined);
        break;
      case orderTypeEnum.lsb:
        set(currentOrder, "streamRate.x", undefined);
        set(currentOrder, "streamRate.y", undefined);
        break;
      case orderTypeEnum.xy:
        set(currentOrder, "streamRate.x", rnd(1, 5));
        const xyOrderYSteps = generateSteppedYArray(20, 500, 10); // Start from 16 as 15 is handled separately
        const yValue =
          rnd(1, 100) <= 80
            ? 15
            : xyOrderYSteps[rnd(0, xyOrderYSteps.length - 1)];
        set(currentOrder, "streamRate.y", yValue);
        break;
      default:
        break;
    }
    const newState = {
      ...orderQty,
      [e.target.id]: currentOrder
    };
    setValue(newState);
    dispatch(setOrdersParams(newState));
  };

  const changeOrderQty = e => {
    if (resetValue === "") setResetValue(orderQty[e.target.id]?.size);
    const currentOrder = cloneDeep(orderQty[e.target.id]);
    set(currentOrder, "size", e.target.value);
    const newState = {
      ...orderQty,
      [e.target.id]: currentOrder
    };
    setValue(newState);
  };

  const changeSize = e => {
    // XY min 1000
    // LSH & LSB min 100
    const currentOrder = cloneDeep(orderQty[e.target.id]);
    let currentQuantity = currentOrder?.size;

    if (order.type === orderTypeEnum.xy && currentQuantity < 1000) {
      set(currentOrder, "size", 1000);
    }
    if (order.type !== orderTypeEnum.xy && currentQuantity < 100) {
      set(currentOrder, "size", 100);
    }
    if (currentQuantity > 900000) {
      set(currentOrder, "size", 900000);
    }

    const newState = {
      ...orderQty,
      [e.target.id]: currentOrder
    };

    if (/^[1-9]+[0-9]*000$/.test(currentQuantity)) {
      dispatch(setOrdersParams(newState));
      setResetValue("");
    } else {
      set(currentOrder, "size", floorQuantity(currentOrder.size));
      const newState = {
        ...orderQty,
        [e.target.id]: currentOrder
      };
      setValue(newState);
      dispatch(setOrdersParams(newState));
    }
  };

  const changeOnEnter = e => {
    if (e.keyCode === 13) {
      e.target.blur();
    }
  };

  const streamOrder = streamOrders?.find(e => e.order_id === order.order_id);

  // list of options from 1 to 500
  const xyRateOptions = Array.from({ length: 500 }, (v, k) => k + 1);

  //list of options from 1 to 100
  const lshRateOptions = Array.from({ length: 100 }, (v, k) => k + 1);

  let minX = 1;
  let maxX = 100;
  let minY = 1;
  let maxY = 100;
  if (order.type === "XY") {
    maxX = 500;
    maxY = 500;
  }

  const yPlaceholder = (
    <span className="y-placeholder-infinity">{config.orders.infinity}</span>
  );

  return (
    <div
      key={order.order_id}
      className="orders-table-item"
      style={{
        display: order.x ? "flex" : "none",
        opacity: streamOrder ? 1 : 0.6
      }}
    >
      <div className="orders-table-row-item order-id">
        <OrderWithSelector
          progress={order?.max_cap?.slice(0, -1)}
          iconSize={20}
          ls={order.max_cap === "LS"}
          marketable={streamOrder?.is_marketable}
          isActive={order.created_at_tp <= currentTick}
          num={i + 1}
          side={order.side}
          id={order.order_id}
          x={order.x}
          simulation={simulation}
          type={order.type}
        />
      </div>
      <div className="orders-table-row-item order-type">
        <select
          id={order.order_id}
          className="order-type-select quantity-input quantity-number total select select-css"
          onChange={changeOrderType}
          name="order_type"
          disabled={simulation}
          value={order.type}
        >
          <option disabled={!showLSHOption} value="LSH">
            LSH
          </option>
          <option value="LSB">LSB</option>
          <option value="XY">XY</option>
        </select>
      </div>

      <div className="orders-table-row-item stream-rate">
        {order.type !== orderTypeEnum.lsb ? (
          <input
            type="number"
            id={order.order_id}
            min={minX}
            max={maxX}
            value={orderQty?.[order.order_id]?.streamRate?.x}
            className="quantity-input quantity-number total input minX"
            onChange={e => changeOrderCap(e, "x")}
            onBlur={e => setOrderCap(e, "x")}
            onKeyUp={changeOnEnter}
            disabled={simulation}
          />
        ) : (
          <div
            className={`quantity-input quantity-number total input minX ${simulation &&
              "disabled-el"}`}
          >
            501
          </div>
        )}
        <ProgressBar
          className="quantity-progress-bar"
          streamOrder={streamOrder}
          simulation={simulation}
          value={streamOrder?.stream_rate_total || 0}
          isActive={order.created_at_tp <= currentTick}
          completed={
            steps[currentTick]
              ? (Number.parseInt(streamOrder?.stream_rate_total || 0) /
                  Number.parseInt(
                    order.type === "LSH" ? 2000 : order?.stream_rate?.y
                  )) *
                100
              : 0
          }
          isLsb={order.type === orderTypeEnum.lsb}
          color={color[order.side]}
        />
        {simulation && (
          <span className="quantity-value quantity-number total">
            {order.max_cap}
          </span>
        )}
        {order.type === orderTypeEnum.xy && (
          <input
            type="number"
            id={order.order_id}
            min={minY}
            max={maxY}
            value={orderQty?.[order.order_id]?.streamRate?.y}
            className="quantity-input quantity-number total input maxY"
            onChange={e => changeOrderCap(e, "y")}
            onBlur={e => setOrderCap(e, "y")}
            onKeyUp={changeOnEnter}
            disabled={simulation}
          />
        )}
        {(order.type === orderTypeEnum.lsh ||
          order.type === orderTypeEnum.lsb) && (
          <span
            className={`quantity-input quantity-number total input maxY ${simulation &&
              "disabled-el"}`}
          >
            {yPlaceholder}
          </span>
        )}
      </div>

      <span className="orders-table-row-item average-price">
        {streamOrder?.average_price || 0}
      </span>
      <span className="orders-table-row-item limit">
        {order.limit ? order.limit.toFixed(2) : "Market"}
      </span>
      <div className="orders-table-row-item quantity">
        <span className="quantity-rest quantity-number">
          {streamOrder?.executed_qty || 0}
        </span>
        <ProgressBar
          className="quantity-progress-bar"
          simulation={simulation}
          value={order?.ordered_qty - streamOrder?.executed_qty || 0}
          completed={
            (streamOrder?.executed_qty / order?.ordered_qty) * 100 || 0
          }
          color={color[order.side]}
          isActive={order.created_at_tp <= currentTick}
        />
        {simulation ? (
          <span className="quantity-value quantity-number total">
            {order.ordered_qty}
          </span>
        ) : (
          <input
            type="text"
            id={order.order_id}
            ref={quantityRef}
            value={orderQty?.[order.order_id]?.size}
            className="quantity-input quantity-number total input"
            onChange={changeOrderQty}
            onBlur={changeSize}
            onKeyUp={changeOnEnter}
          />
        )}
      </div>
    </div>
  );
};
